<template>
  <b-row class="align-items-center">
    <b-col cols="12">
      <div class="d-flex align-items-center justify-content-end">
        <b-button variant="primary" to="/invoice/add">
          <span class="text-nowrap">Add Invoice</span>
        </b-button>
      </div>
    </b-col>

    <b-col cols="12" md="2" sm="4" class="my-1">
      <b-form-group class="mb-0">
        <label class="d-inline-block text-sm-left mr-50">Per page</label>
        <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions"  />
      </b-form-group>
    </b-col>

    <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1;">
      <b-button v-if="HasPermission(PermissionList.Sales_Search)" variant="outline-danger" @click="clearFilters" :disabled="!anyFilterActive">
        Clear Filters
      </b-button>
    </b-col>

    <b-col cols="12">
      <b-table striped hover responsive :per-page="perPage" :current-page="currentPage" :items="filteredInvoices"
        :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection"
        @filtered="onFiltered">

        <!-- Invoice No Filter -->
        <template #head(invoiceNo)>
          <div class="d-flex flex-column">
            <span>Invoice No</span>
            <b-form-input v-model="columnFilters.invoiceNo" placeholder="Filter by Invoice No" size="sm"
              @input="filterTable" />
          </div>
        </template>

        <!-- Customer Name Filter -->
        <template #head(leadClientName)>
          <div class="d-flex flex-column">
            <span>Customer Name</span>
            <b-form-input v-model="columnFilters.customerName" placeholder="Filter by Customer Name" size="sm"
              @input="filterTable" />
          </div>
        </template>

        <!-- Sales Person Filter -->
        <template #head(salesPersonId)>
          <div class="d-flex flex-column">
            <span>Sales Person</span>
            <b-form-input v-model="columnFilters.salesPerson" placeholder="Filter by Sales Person" size="sm"
              @input="filterTable" />
          </div>
        </template>

        <!-- Due Date Filter -->
        <template #head(dueDate)>
          <div class="d-flex flex-column">
            <span>Due Date</span>
            <date-picker v-model="columnFilters.dueDate" :range="true" format="YYYY-MM-DD"
              placeholder="Select Date Range" @change="filterTable" :clearable="false" :input-attr="{ readonly: true }"
              @focus="openCalendar" class="small-datepicker" />
          </div>
        </template>

        <!-- Created On Filter -->
        <template #head(createdOn)>
          <div class="d-flex flex-column">
            <span>Created On</span>
            <date-picker v-model="columnFilters.createdOn" :range="true" format="YYYY-MM-DD"
              placeholder="Select Date Range" @change="filterTable" :clearable="false" :input-attr="{ readonly: true }"
              @focus="openCalendar" class="small-datepicker" />
          </div>
        </template>

        <!-- Status Filter -->
        <template #head(status)>
          <div class="d-flex flex-column">
            <span>Status</span>
            <b-form-select v-model="columnFilters.status" :options="statusOptions" @change="filterTable" size="sm">
              <option :value="null" disabled>Select Status</option>
            </b-form-select>
          </div>
        </template>

        <!-- Table Cells for Date and Actions -->
        <template v-slot:cell(dueDate)="data">
          {{ convertTimestampToDate(data.item.dueDate) }}
        </template>
        <template #cell(salesPersonId)="data">
          {{ getSalePersonName(data.item.salesPersonId) }}
        </template>
        <template #cell(createdOn)="data">
          {{ convertTimestampToDate(data.item.createdOn) }}
        </template>
        <template #cell(total)="data">
          {{ (data.item.currency == null) ? "Rs" : getCurrencySymbol(data.item.currency) }}
          {{ data.item.total.toFixed(2) }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-button v-if="HasPermission(PermissionList.Sales_View)" variant="primary" :to="getInvoiceId(data.item.id)">
            <feather-icon icon="EyeIcon" class="mr-50" /> View
          </b-button>
        </template>
        <template v-slot:cell(status)="data">
          <b-badge v-if="data.item.status === 0" variant="info">New</b-badge>
          <b-badge v-if="data.item.status === 1" variant="warning">Pending</b-badge>
          <b-badge v-if="data.item.status === 2" variant="primary">Completed</b-badge>
          <b-badge v-if="data.item.status === 3" variant="danger">Cancelled</b-badge>
        </template>
      </b-table>

      <div class="d-flex justify-content-center mb-1">
        <b-alert variant="warning" show v-if="filteredInvoices.length === 0" class="d-flex justify-content-center">
          <h4 class="alert-heading">No Invoices found</h4>
        </b-alert>
      </div>

      <div class="d-flex justify-content-center mb-1">
        <b-spinner variant="success" label="Spinning" block v-if="loading"
          style="width: 3rem; height: 3rem"></b-spinner>
      </div>
    </b-col>

    <b-col cols="12">
      <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
        class="my-0" />
      <b-form-group>
        Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
      </b-form-group>
    </b-col>
  </b-row>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import dayjs from "dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BAlert,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { PermissionList } from '@/libs/defaultList';
import { HasPermission } from '@/@core/utils/validations/permissionCheck';
import { CurrencySymbols } from '@/libs/defaultList';

export default {
  components: {
    BTable,
    BAvatar,
    BAlert,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BSpinner,
    DatePicker,
  },
  data() {
    return {
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 1,
      dayjs,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      columnFilters: {
        invoiceNo: "",
        customerName: "",
        salesPerson: "",
        dueDate: null,
        createdOn: null,
        status: null,
      },
      fields: [
      { key: "invoiceNo", label: "Invoice No", sortable: true },
      { key: "leadClientName", label: "Customer Name", sortable: true },
      { key: "salesPersonId", label: "Sales Person", sortable: true },
      { key: "total", label: "Total", sortable: true },
      { key: "dueDate", label: "Due Date", sortable: true },
      { key: "createdOn", label: "Created On", sortable: true },
      { key: "status", label: "Status", sortable: true },
      { key: "actions", label: "Actions", sortable: false },
    ],
      statusOptions: [
        { value: null, text: "Select Status" },
        { value: 0, text: "New" },
        { value: 1, text: "Pending" },
        { value: 2, text: "Completed" },
        { value: 3, text: "Cancelled" },
      ],
    };
  },
  computed: {
    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      loading: "loading",
    }),
    ...mapGetters("invoiceModule", {
      invoiceList: "invoices",
    }),
    anyFilterActive() {
      const { invoiceNo, customerName, salesPerson, dueDate, createdOn, status } = this.columnFilters;
      return (
        invoiceNo || customerName || salesPerson || dueDate || createdOn || status !== null
      );
    },
    filteredInvoices() {
      return this.invoiceList.filter((item) => {
        const matchesInvoiceNo = !this.columnFilters.invoiceNo ||
          String(item.invoiceNo).toLowerCase().includes(this.columnFilters.invoiceNo.toLowerCase());
        const matchesCustomerName = !this.columnFilters.customerName ||
          String(item.leadClientName || "").toLowerCase().includes(this.columnFilters.customerName.toLowerCase());
        const matchesSalesPerson = !this.columnFilters.salesPerson ||
          this.getSalePersonName(item.salesPersonId).toLowerCase().includes(this.columnFilters.salesPerson.toLowerCase());
        const matchesStatus = this.columnFilters.status === null ||
          item.status === this.columnFilters.status;
          const matchesDueDate = this.columnFilters.dueDate && this.columnFilters.dueDate.length === 2
  ? dayjs(item.dueDate).isSameOrAfter(this.columnFilters.dueDate[0], 'day') &&
    dayjs(item.dueDate).isSameOrBefore(this.columnFilters.dueDate[1], 'day')
  : true;
  


        // Filter by Created On (if it's a range filter)
        const matchesCreatedOn = this.columnFilters.createdOn && this.columnFilters.createdOn.length === 2
        ? dayjs(item.createdOn).isSameOrAfter(this.columnFilters.createdOn[0], 'day') &&
    dayjs(item.createdOn).isSameOrBefore(this.columnFilters.createdOn[1], 'day')
  : true;

        return matchesInvoiceNo && matchesCustomerName && matchesSalesPerson && matchesDueDate && matchesCreatedOn && matchesStatus;
      });
    },
  },
  methods: {
    ...mapActions("salesPersonModule", ["getSalesPersonListAction"]),
    ...mapActions("invoiceModule", ["getInvoicesListAction"]),
    getSalePersonName(id) {
      const salesPerson = this.SalesPersonList.find((s) => s.id === id);
      return salesPerson ? salesPerson.firstName + " " + salesPerson.lastName : "";
    },
    getCurrencySymbol(currency) {
      return CurrencySymbols[currency];
    },
    convertTimestampToDate(timestamp) {
      return new Date(timestamp).toLocaleDateString();
    },
    isWithinRange(date, range) {
      if (!date || !range || range.length !== 2) return false;
      const [start, end] = range.map((d) => new Date(d));
      const itemDate = new Date(date);
      return itemDate >= start && itemDate <= end;
    },
    filterTable() {
      this.currentPage = 1;
      this.totalRows = this.filteredInvoices.length;
    },
    clearFilters() {
      // Clear each filter individually to trigger reactivity
      this.columnFilters.invoiceNo = "";
      this.columnFilters.customerName = "";
      this.columnFilters.salesPerson = "";
      this.columnFilters.dueDate = null;
      this.columnFilters.createdOn = null;
      this.columnFilters.status = null;

      // Reapply filters and reset pagination
      this.filterTable();
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    getInvoiceId(id) {
      return "/invoice/view/" + id;
    },
    openCalendar(event) {
      // Ensure the calendar opens on focus
      event.target.blur(); // Remove focus to prevent further typing
      event.target.click(); // Programmatically open the calendar

      // Add an event listener to detect clicks outside the date picker
      document.addEventListener('click', this.closeCalendar);
    },
    closeCalendar(event) {
      const datePicker = this.$refs.datePicker.$el;

      // Check if the click happened outside the date picker
      if (!datePicker.contains(event.target)) {
        // Close the calendar by clicking outside
        this.$refs.datePicker.hideCalendar();

        // Remove the event listener after closing
        document.removeEventListener('click', this.closeCalendar);
      }
    },
  },
  async mounted() {
    await this.getInvoicesListAction();
    this.totalRows = this.invoiceList.length;
    await this.getSalesPersonListAction();
  },
  setup(props){
    return{
      PermissionList,
      HasPermission
    }
  }
};
</script>


<style scoped>
.small-datepicker {
  width: 150px;
  /* Adjust width as desired */
}
</style>
